import type { Content } from "@puckeditor/core";
import type { PuckBlockProps, PuckLandingPageData } from "./types";
import type { PageMedia } from "../types";
import { pageContentSchemas } from "../utils/validatePageContent";

type PuckItem = Content<PuckBlockProps>[number];

/** Native Lexical JSON stays untouched until an editor explicitly replaces its text. */
export function emptyLexicalContent(text = ""): Record<string, unknown> {
  return {
    root: {
      type: "root",
      version: 1,
      direction: null,
      format: "",
      indent: 0,
      children: text.split("\n").map((line) => ({
        type: "paragraph",
        version: 1,
        direction: null,
        format: "",
        indent: 0,
        children: line
          ? [
              {
                type: "text",
                text: line,
                version: 1,
                detail: 0,
                format: 0,
                mode: "normal",
                style: "",
              },
            ]
          : [],
      })),
    },
  };
}

export function collectPuckMediaIds(data: PuckLandingPageData): string[] {
  const ids = new Set<string>();
  const pending: PuckItem[] = [...data.content];
  while (pending.length) {
    const item = pending.pop()!;
    if ((item.type === "Image" || item.type === "Testimonial") && item.props.mediaId) {
      ids.add(item.props.mediaId);
    }
    if (item.type === "Form" && item.props.bannerMediaId) ids.add(item.props.bannerMediaId);
    if (item.type === "RichText" || item.type === "Disclosure") {
      const visit = (value: unknown) => {
        if (!value || typeof value !== "object" || Array.isArray(value)) return;
        const node = value as Record<string, unknown>;
        if (
          node.type === "upload" &&
          node.relationTo === "media" &&
          typeof node.value === "string"
        ) {
          ids.add(node.value);
        }
        if (Array.isArray(node.children)) node.children.forEach(visit);
        if (node.root) visit(node.root);
      };
      visit(item.props.content);
    }
    switch (item.type) {
      case "Section":
      case "Card":
      case "Visibility":
      case "SupportingContent":
        pending.push(...(item.props.content as PuckItem[]));
        break;
      case "Columns":
        pending.push(...(item.props.left as PuckItem[]), ...(item.props.right as PuckItem[]));
        break;
      case "Feature":
        pending.push(
          ...(item.props.media as PuckItem[]),
          ...(item.props.main as PuckItem[]),
          ...(item.props.after as PuckItem[])
        );
        break;
      case "Grid":
        pending.push(...(item.props.items as PuckItem[]));
        break;
    }
  }
  return [...ids];
}

/** Populate upload references from trusted media without writing documents into the saved design. */
export function hydratePuckRichTextMedia(
  content: Record<string, unknown>,
  media: readonly PageMedia[]
): Record<string, unknown> {
  const images = new Map(
    media.flatMap((image) => {
      const parsed = pageContentSchemas.image.safeParse({
        image: {
          id: image.id,
          url: image.url,
          alt: image.alt,
          width: image.width,
          height: image.height,
        },
      });
      return parsed.success ? [[image.id, parsed.data.image] as const] : [];
    })
  );
  const visit = (input: unknown): unknown => {
    if (Array.isArray(input)) return input.map(visit);
    if (!input || typeof input !== "object") return input;
    const node = input as Record<string, unknown>;
    if (node.type === "upload") {
      const image = typeof node.value === "string" ? images.get(node.value) : undefined;
      if (!image) {
        return {
          type: "paragraph",
          version: 1,
          children: [{ type: "text", version: 1, text: "Image unavailable" }],
        };
      }
      // Payload's reader needs mimeType; only image documents enter PageMedia.
      return { ...node, value: { ...image, mimeType: "image/" } };
    }
    return Object.fromEntries(Object.entries(node).map(([key, value]) => [key, visit(value)]));
  };
  return visit(content) as Record<string, unknown>;
}
